import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarehouseWithOccupancyStatistic } from 'entities/Warehouse';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'shared/ui/Button';
import { ReactComponent as KeyIcon } from 'shared/assets/icons/KeyIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { ReactComponent as UploadIcon } from 'shared/assets/icons/UploadIcon.svg';
import { ReactComponent as SettingsIcon } from 'shared/assets/icons/SettingsIcon.svg';
import { ProgressBar } from 'shared/ui/ProgressBar';
import { ContextMenu } from 'shared/ui/ContextMenu';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { warehouseStatisticCardActions } from '../model/slice/warehouseStatisticCardSlice';
import { useRole } from 'entities/User';
import { MimeTypes, uploadModalActions } from 'features/UploadModal';
import { Divider } from 'antd';

interface WarehouseStatisticCardProps {
  warehouseData: WarehouseWithOccupancyStatistic;
}

export const WarehouseStatisticCard: FC<WarehouseStatisticCardProps> = memo((props) => {
  const {
    warehouseData: {
      warehouseId,
      name,
      address,
      occupiedBoxesAmount,
      activeBoxesAmount,
      occupiedSquare,
      activeSquare,
      occupiedBoxesAmountToday,
      occupiedSquareToday,
      totalBoxesAmount,
      totalSquare,
    },
  } = props;

  const dispatch = useAppDispatch();

  const { t } = useAppTranslation(['warehouses', 'common']);

  const { isManagement } = useRole();

  const navigate = useNavigate();

  const { getEncodedString } = useQueryString();

  const boxesPercent = roundNumber((occupiedBoxesAmount / activeBoxesAmount) * 100, 0);
  const squarePercent = roundNumber((occupiedSquare / activeSquare) * 100, 0);
  const boxesPercentToday = roundNumber((occupiedBoxesAmountToday / activeBoxesAmount) * 100, 0);
  const squarePercentToday = roundNumber((occupiedSquareToday / activeSquare) * 100, 0);

  const openUploadModal = useCallback((): void => {
    dispatch(
      uploadModalActions.openModal({
        title: t('Upload warehouse boxes'),
        description: t('Upload CSV or EXCEL file!'),
        submitArgs: { warehouseId },
        allowedFormats: [MimeTypes.CSV, MimeTypes.EXCEL_XLS, MimeTypes.EXCEL_XLSX],
        draggerDescription: t('Excel file size must be no more than 10MB'),
      }),
    );
  }, [dispatch, t, warehouseId]);

  const contextMenuItems = useMemo(
    () => [
      { key: '1', label: <Link to="#">{t('Settings')}</Link>, icon: <SettingsIcon /> },
      { key: '2', label: <Link to="#">{t('Reports')}</Link>, icon: <DocumentIcon /> },
      {
        key: '3',
        label: (
          <Button
            icon={<UploadIcon className="w-6 h-6" />}
            iconPosition="prev"
            fontColor="text-primary"
            theme="clear"
            onClick={openUploadModal}
          >
            {t('Import boxes')}
          </Button>
        ),
      },
    ],
    [openUploadModal, t],
  );

  const goToBoxesPage = useCallback(() => {
    const encodedWarehouse = getEncodedString({ selectedWarehouseId: { value: warehouseId, label: name, title: t('Warehouse') } });

    navigate(`${AppRoutes.BOXES}?selectedWarehouseId=${encodedWarehouse.selectedWarehouseId}`);
  }, [getEncodedString, name, navigate, t, warehouseId]);

  const openDoorsModal = useCallback((): void => {
    dispatch(warehouseStatisticCardActions.setDoorsModalOpened(warehouseId));
  }, [dispatch, warehouseId]);

  return (
    <div className="flex flex-col w-[350px] desktop:w-[380px] bg-secondaryLight py-10 px-6 rounded-lg">
      <div className="bg-white py-7 px-6 rounded-lg space-y-4">
        <div className="flex justify-between items-center">
          <div className="font-semibold text-lg h-12">
            {name} {address}
          </div>
          {isManagement && <ContextMenu items={contextMenuItems} />}
        </div>
        <Divider />
        <div className="flex flex-col justify-between">
          <div>
            <span>{t('Total boxes', { ns: 'common' })}:</span>
            <span className="ml-1">{totalBoxesAmount}</span>
          </div>
          <div>
            <span>{t('Total square', { ns: 'common' })}:</span>
            <span className="ml-1">{totalSquare} m²</span>
          </div>
        </div>
        <ProgressBar
          type="double"
          items={[
            {
              description: t('{{occupiedBoxesAmount}} boxes out of {{totalBoxesAmount}} are occupied for today', {
                occupiedBoxesAmount: occupiedBoxesAmountToday,
                totalBoxesAmount: activeBoxesAmount,
              }),
              percent: boxesPercentToday,
            },
            {
              description: t('{{occupiedBoxesAmount}} boxes out of {{totalBoxesAmount}} are expected to be occupied', {
                occupiedBoxesAmount,
                totalBoxesAmount: activeBoxesAmount,
              }),
              percent: boxesPercent,
            },
          ]}
        />
        <ProgressBar
          type="double"
          items={[
            {
              description: t('{{occupiedSquare}} m² out of {{totalSquare}} m² are occupied for today', {
                occupiedSquare: occupiedSquareToday,
                totalSquare: activeSquare,
              }),
              percent: squarePercentToday,
            },
            {
              description: t('{{occupiedSquare}} m² out of {{totalSquare}} m² are expected to be occupied', {
                occupiedSquare,
                totalSquare: activeSquare,
              }),
              percent: squarePercent,
            },
          ]}
        />
        <div className="space-y-2.5">
          <Button theme="secondary" onClick={goToBoxesPage}>
            {t('Boxes')}
          </Button>
          <Button icon={<KeyIcon className="stroke-white" />} iconPosition="prev" onClick={openDoorsModal}>
            {t('Doors')}
          </Button>
        </div>
      </div>
    </div>
  );
});
